import React, { Fragment, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { WithdrawBankSvg } from "../../components/Dashboard/svgs/WithdrawBankSvg";
import { NoAccountWarning } from "../../components/Dashboard/withdraw-now/NoAccountWarning";
import { Skeleton } from "../../components/Dashboard/withdraw-now/Skeleton";
import { WithdrawalAccount } from "../../components/Dashboard/withdraw-now/WithdrawalAccount";
import {
  useGetBanksQuery,
  useListBanksQuery,
} from "../../features/account/bankSlice";
import {
  useCreateWithdrawMutation,
  useCreateWithdrawWithOtpMutation,
  useValidateWithdrawMutation
} from "../../features/withdraws/withdrawSlice";
import jwtDecode from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/store";
import {
  selectCurrentUser,
  selectCurrentToken,
  updateCredentials,
} from "../../features/auth/authSlice";
import { openModal } from "../../features/two-factor-modal/twoFactorModalSlice";
import { openOtpModal } from "../../features/withdraw-otp/withdrawOtpSlice";
import { Link, useSearchParams } from "react-router-dom";
import { useCurrentUserQuery } from "../../features/chat/chatSlice";
import { WithdrawalType } from "../../components/Dashboard/transactions/types";
import { FiChevronDown } from "react-icons/fi";
import { WithdrawOtpModal } from "../../components/Dashboard/withdraw-now/WithdrawOtpModal";
import { getCoinIcon, getCoinLabel } from "../../components/Dashboard/coins/CoinIcons";
import { TopUpModal } from "../../components/Dashboard/withdraw/TopUpModal";
import { useGetMomoConfigQuery } from "../../features/momo/momoSlice";

export const WithdrawNow = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const [isTopUpOpen, setIsTopUpOpen] = useState<boolean>(false);
  const { data: currentUser, isLoading: isCurrentUserLoading } =
    useCurrentUserQuery({
      time: Date.now(),
    });

  const [searchParams, setSearchParams] = useSearchParams();
  const SOURCE_PLACEHOLDER = "Select Source";
  const VALID_SOURCES = ["account", "reward", "referral"] as const;
  const SOURCE_LABELS: Record<string, string> = {
    [SOURCE_PLACEHOLDER]: SOURCE_PLACEHOLDER,
    account: "Main Wallet",
    reward: "Reward",
    referral: "Referral",
  };
  const source = searchParams.get("source") || SOURCE_PLACEHOLDER;

  const is2FAModalOpen = useSelector(
    (state: RootState) => state.twoFactorModal.isOpen
  );

  const {
    data: banks,
    isLoading,
    isSuccess,
    isFetching,
    isError,
    error,
  } = useGetBanksQuery();

  const [
    createWithdraw,
    {
      isLoading: isCreateWithdrawLoading,
      isError: isWithdrawError,
      isSuccess: isWithdrawSuccess,
    },
  ] = useCreateWithdrawMutation();

  const [
    createWithdrawWithOtp
  ] = useCreateWithdrawWithOtpMutation();

  const [validateWithdraw] = useValidateWithdrawMutation();

  // Ghana mobile money is its own flow (/app/momo-withdraw); the option only
  // shows while the backend has it switched on.
  const { data: momoConfig } = useGetMomoConfigQuery();
  const momoEnabled = momoConfig?.enabled === true;
  const withdrawalTypeOptions = Object.values(WithdrawalType).filter(
    (item) => item !== WithdrawalType.MobileMoney || momoEnabled
  );


  const {
    data: bankLists,
    isLoading: isLoadingBanks,
    isSuccess: isSuccessBanks,
  } = useListBanksQuery();

  const QUICK_CRYPTO_COINS = [
    "bitcoin",
    "usdt",
    "litecoin",
    "ethereum",
    "dogecoin",
    "Cashapp Bitcoin Lightning",
  ];

  const [selectedAccount, setSelectedAccount] = useState("");
  const [pendingWithdrawData, setPendingWithdrawData] = useState<any>(null);
  const [coinDropdownOpen, setCoinDropdownOpen] = useState(false);
  const coinDropdownRef = useRef<HTMLDivElement>(null);
  console.log(selectedAccount);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (coinDropdownRef.current && !coinDropdownRef.current.contains(event.target as Node)) {
        setCoinDropdownOpen(false);
      }
    };
    if (coinDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [coinDropdownOpen]);

  // Legacy Bitcoin/USDT pseudo-banks are unusable for fiat withdrawals
  // (crypto now goes through /app/crypto-payout) — hide them here.
  const withdrawableBanks =
    banks?.data?.filter(
      (b: any) => !/^(bitcoin|usdt)$/i.test(b?.bankName?.trim() || "")
    ) || [];

  useEffect(() => {
    if (!isLoading && withdrawableBanks.length > 0)
      setSelectedAccount(withdrawableBanks[0]._id);
  }, [banks]);

  // Helper function to execute withdrawal with the appropriate method
  // Build the withdraw payload (shared by the pre-OTP validate call and the
  // actual create call so they're always identical).
  const buildWithdrawData = (values: any) => {
    const withdrawData: Record<string, any> = {
      amount: values.amount,
      type: values.withdrawalType === WithdrawalType.QuickCash ? "automatic" : "manual",
      note: values?.note,
      narration: values?.narration,
      withdrawalType: values?.withdrawalType,
      source: values?.source,
    };

    // Add specific fields based on withdrawal type
    switch (values.withdrawalType) {
      case WithdrawalType.DirectWithdrawal:
        Object.assign(withdrawData, { bank: selectedAccount });
        break;
      case WithdrawalType.QuickCash:
        Object.assign(withdrawData, {
          bankCode: values?.bankCode,
          accountNumber: values?.accountNumber,
        });
        break;
      case WithdrawalType.QuickMobile:
        Object.assign(withdrawData, { withdrawalDetails: values?.withdrawalDetails });
        break;
      case WithdrawalType.QuickCrypto:
        Object.assign(withdrawData, {
          withdrawalDetails: values?.withdrawalDetails,
          coin: values?.coin,
        });
        break;
    }
    return withdrawData;
  };

  const executeWithdrawal = async (values: any, challengeId?: string, otpCode?: string) => {
    const withdrawData = buildWithdrawData(values);

    if (challengeId && otpCode) {
      // Use OTP-enabled mutation
      return await createWithdrawWithOtp({
        body: withdrawData,
        challengeId,
        otpCode,
      }).unwrap();
    } else {
      // Use regular mutation
      return await createWithdraw(withdrawData).unwrap();
    }
  };

  // Handle OTP submission
  const handleOtpSubmit = async (challengeId: string, otpCode: string) => {
    if (!pendingWithdrawData) {
      throw new Error("No pending withdrawal data found");
    }

    try {
      toast.loading("Processing your withdraw request...");
      await executeWithdrawal(pendingWithdrawData, challengeId, otpCode);
      formik.resetForm();
      setPendingWithdrawData(null);
      toast.dismiss();
      toast.success("Withdraw request created successfully!");
    } catch (error: any) {
      toast.dismiss();
      if (typeof error?.data?.message == "string") {
        toast.error(error.data.message);
      } else {
        error?.data.message.map((err: string) => {
          toast.error(err);
        });
      }
      throw error;
    }
  };

  const formik = useFormik({
    initialValues: {
      amount: 0,
      bank: "",
      note: "",
      narration: "",
      acceptTerms: false,
      // No default: users must pick, so they see every withdrawal type
      // instead of silently getting Direct Withdrawal.
      withdrawalType: "" as WithdrawalType | "",
      coin: undefined,
      withdrawalDetails: undefined,
      bankCode: undefined,
      accountNumber: undefined,
      source: source || SOURCE_PLACEHOLDER,
    },
    validationSchema: Yup.object().shape({
      source: Yup.string()
        .oneOf([...VALID_SOURCES], "Please select a source (account, reward, or referral).")
        .required("Please select a source."),
      withdrawalType: Yup.string().required("Please select a withdrawal type."),
      amount: Yup.string().required(
        "Please provide how much you want to withdraw."
      ),
      narration: Yup.string().required(
        "Narration is required."
      ),
      note: Yup.string(),
      acceptTerms: Yup.boolean().isTrue(
        "You must agree to our Terms & Conditions."
      ),
    }),
    onSubmit: async (values) => {
      try {
        if (!values.withdrawalType) {
          toast.error("Select Withdrawal Type");
          return;
        }
        if (values.source === SOURCE_PLACEHOLDER) {
          toast.error("Please select a source (account, reward, or referral).");
          return;
        }

        // Pre-OTP validation: surface errors (insufficient balance, unsupported
        // bank, limits) BEFORE prompting for 2FA/OTP. Backward-compatible — if
        // this endpoint is unavailable on an older backend, fall through to the
        // existing create flow (which validates after OTP).
        try {
          await validateWithdraw(buildWithdrawData(values)).unwrap();
        } catch (error: any) {
          if (error?.status !== 404) {
            const msg = error?.data?.message;
            if (typeof msg === "string") toast.error(msg);
            else if (Array.isArray(msg)) msg.forEach((m: string) => toast.error(m));
            else toast.error("This withdrawal can't be processed. Please review your details.");
            return;
          }
          // 404 → older backend without /validate: continue (validated post-OTP).
        }

        const decoded: {
          iat: number;
          exp: number;
          is2FAAuthenticated?: boolean;
          userId: string;
        } = jwtDecode(token!);
        if (
          user?.is2FAEnabled &&
          (!decoded.is2FAAuthenticated ||
            decoded.exp < new Date().getTime() / 1000)
        ) {
          dispatch(openModal());
        } else {
          toast.loading("Processing your withdraw request...");

          // Validate withdrawal type
          if (!values.withdrawalType) {
            toast.dismiss();
            toast.error("Select Withdrawal Type");
            return;
          }

          if (values.source === SOURCE_PLACEHOLDER) {
            toast.dismiss();
            toast.error("Please select a source (account, reward, or referral).");
            return;
          }

          try {
            await executeWithdrawal(values);
            formik.resetForm();
            toast.dismiss();
            toast.success("Withdraw request created successfully!");
          } catch (error: any) {
            toast.dismiss();
            
            // Check if this is an OTP required error
            if (
              error?.status === 401 && 
              error?.data?.message?.includes("OTP required") &&
              error?.data?.challengeId &&
              error?.data?.instructions
            ) {
              // Store pending withdrawal data
              setPendingWithdrawData(values);
              
              // Open OTP modal
              dispatch(openOtpModal({
                challengeId: error.data.challengeId,
                ttlSeconds: error.data.instructions.ttlSeconds || 300,
              }));
              
              toast.error("OTP verification required. Check your email.");
            } else {
              // Handle other errors
              if (typeof error?.data?.message == "string") {
                toast.error(error.data.message);
              } else if (Array.isArray(error?.data?.message)) {
                error?.data.message.map((err: string) => {
                  toast.error(err);
                });
              } else {
                toast.error("An error occurred during withdrawal");
              }
            }
          }
        }
      } catch (error: any) {
        toast.dismiss();
        toast.error("An unexpected error occurred");
        console.error("Withdrawal error:", error);
      }
    },
  });

  useEffect(() => {
    if (
      currentUser &&
      user &&
      user?.isKYCVerified != currentUser?.isKYCVerified
    ) {
      // Update user state
      dispatch(
        updateCredentials({
          user: currentUser,
        })
      );
    }
  }, [currentUser]);

  // @ts-ignore
  return (
    <div className="w-full relative md:px-4">
      <div className="mt-8 pt-2 pb-12 mb-8 bg-black container-lg mx-auto">
        <div className="flex items-center justify-between px-4 pt-2 pb-4">
          <div className="flex items-center gap-3">
            <div className="box-content p-2.5 rounded-full bg-primary">
              <WithdrawBankSvg />
            </div>

            <p className="text-base text-white/40 font-light">
              Withdraw balance
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsTopUpOpen(true)}
            className="btn-secondary px-4 py-2"
          >
            Top Up
          </button>
        </div>

        <hr className="border-white/10" />

        <form
          onSubmit={formik.handleSubmit}
          className="max-w-md mx-auto mt-8 px-4 md:px-0"
        >
          {/* Warning about maximum limit of 500000 Naira w ithout KYC and verify KYC Button */}

          {!isCurrentUserLoading && !user?.isKYCVerified && (
            <div
              className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300"
              role="alert"
            >
              <p className="text-sm text-white/70">
                Maximum withdrawal limit is 3,000,000 NGN without KYC
              </p>
              <div className="flex items-center gap-2 mt-4">
                <Link to="/app/account/kyc">
                  <button
                    type="button"
                    className="btn-primary px-4 py-2 rounded-md"
                  >
                    Verify KYC
                  </button>
                </Link>
                <p className="text-sm text-white/70">
                  to increase your withdrawal limit
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="source" className="text-sm text-white/70">
              Source
            </label>
            <select
              className="capitalize form-input spin-button-none"
              name="source"
              id="source"
              value={formik.values?.source}
              onChange={(e) => {
                setSearchParams({ source: e.target.value });
                formik.handleChange(e);
              }}
            >
              {[SOURCE_PLACEHOLDER, ...VALID_SOURCES]?.map((item: string) => (
                <option className="capitalize" key={item} value={item}>
                  {SOURCE_LABELS[item] ?? item}
                </option>
              ))}
            </select>
            <p className="text-red-500 text-xs">
              {formik.touched.source &&
                Boolean(formik.errors.source) &&
                formik.errors.source}
            </p>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="amount" className="text-sm text-white/70">
              Amount (NGN)
            </label>
            <input
              id="amount"
              name="amount"
              type="number"
              step="0.01"
              min="0.0"
              onWheel={(event) => event.currentTarget.blur()}
              placeholder="0.0"
              value={formik.values.amount}
              onChange={formik.handleChange}
              className="form-input spin-button-none"
            />

            <p className="text-red-500 text-xs">
              {formik.touched.amount &&
                Boolean(formik.errors.amount) &&
                formik.errors.amount}
            </p>
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <label htmlFor="withdrawalType" className="text-sm text-white/70">
              Withdrawal Type
            </label>
            <select
              className="capitalize form-input spin-button-none"
              name="withdrawalType"
              id="withdrawalType"
              value={formik.values?.withdrawalType}
              onChange={formik.handleChange}
            >
              <option value="" disabled>
                Select withdrawal type
              </option>
              {withdrawalTypeOptions?.map((item: any) => (
                <option className="capitalize" key={item} value={item}>
                  {item?.split("-")?.join(" ")}{" "}
                  {item === "quick-crypto" &&
                    " (Withdraw money to Bitcoin, USDT, Litecoin, Ethereum, Dogecoin etc)"}
                  {item === WithdrawalType.MobileMoney && " (Ghana — MTN, AirtelTigo)"}
                </option>
              ))}
            </select>
            <p className="text-red-500 text-xs">
              {formik.touched.withdrawalType &&
                Boolean(formik.errors.withdrawalType) &&
                formik.errors.withdrawalType}
            </p>
          </div>

          {formik?.values?.withdrawalType ===
          WithdrawalType.DirectWithdrawal ? (
            <>
              <p className="text-sm text-white/70 mt-4">Select an account</p>
              <div className="flex flex-col gap-2 mt-2 max-h-[300px] overflow-y-auto">
                {isLoading || isFetching ? (
                  <div className="flex flex-col gap-2">
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                  </div>
                ) : withdrawableBanks.length > 0 ? (
                  withdrawableBanks.map((data: any, index: number) => (
                    <WithdrawalAccount
                      key={index}
                      _id={data._id}
                      name={data.bankName}
                      bankNo={data.accountNumber}
                      isSelected={data._id === selectedAccount}
                      setSelectedAccount={setSelectedAccount}
                    />
                  ))
                ) : (
                  <NoAccountWarning />
                )}
              </div>
              <p className="text-red-500 text-xs mt-1">
                {!selectedAccount && (
                  <span>You must select a bank account</span>
                )}
              </p>
            </>
          ) : formik?.values?.withdrawalType === WithdrawalType.QuickCrypto ? (
            <>
              <div className="mt-4 rounded-lg border border-primary/40 bg-gray-700/40 p-4">
                <p className="text-sm text-white/80">
                  Crypto payouts now run through a dedicated flow with a live
                  rate quote, network fees and on-chain tracking.
                </p>
                <Link
                  to={`/app/crypto-payout${VALID_SOURCES.includes(formik.values.source as any) ? `?source=${formik.values.source}` : ""}`}
                  className="btn-primary mt-3 inline-flex items-center gap-2 w-full justify-center py-4 text-base font-medium"
                >
                  Pay out crypto →
                </Link>
              </div>
            </>
          ) : formik?.values?.withdrawalType === WithdrawalType.MobileMoney ? (
            <>
              <div className="mt-4 rounded-lg border border-primary/40 bg-gray-700/40 p-4">
                <p className="text-sm text-white/80">
                  Ghana mobile money payouts run through a dedicated flow with
                  a live cedi quote and recipient name check.
                </p>
                <Link
                  to={`/app/momo-withdraw${VALID_SOURCES.includes(formik.values.source as any) ? `?source=${formik.values.source}` : ""}`}
                  className="btn-primary mt-3 inline-flex items-center gap-2 w-full justify-center py-4 text-base font-medium"
                >
                  Withdraw to mobile money →
                </Link>
              </div>
            </>
          ) : formik?.values?.withdrawalType === WithdrawalType.QuickMobile ? (
            <div className="flex flex-col gap-2 mt-4">
              <label
                htmlFor="withdrawalDetails"
                className="text-sm text-white/70"
              >
                Quick Mobile Account
              </label>
              <input
                id="withdrawalDetails"
                name="withdrawalDetails"
                type="text"
                onWheel={(event) => event.currentTarget.blur()}
                placeholder="Withdrawal Dertails"
                value={formik.values.withdrawalDetails}
                onChange={formik.handleChange}
                className="form-input spin-button-none"
              />

              {/*<p className='text-red-500 text-xs'>*/}
              {/*  {formik.touched.amount &&*/}
              {/*      Boolean(formik.errors.amount) &&*/}
              {/*      formik.errors.amount}*/}
              {/*</p>*/}
            </div>
          ) : formik?.values?.withdrawalType === WithdrawalType.QuickCash ? (
            <>
              <div className="flex flex-col gap-2 mt-4">
                <label htmlFor="bankCode" className="text-xs text-white/70">
                  Select Bank / Payment Method
                </label>
                <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary">
                  <select
                    name="bankCode"
                    id="bankCode"
                    value={formik.values.bankCode}
                    onChange={(e) => {
                      if (e.target.selectedOptions[0].value === "6") {
                        formik.setFieldValue("bankName", "");
                      } else {
                        formik.setFieldValue(
                          "bankName",
                          e.target.selectedOptions[0].text
                        );
                      }
                      formik.handleChange(e);
                    }}
                    className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full"
                  >
                    {bankLists?.map((bank: any) => {
                      if (bank?.type === "automatic")
                        return (
                          <option value={bank.bank_code}>
                            {bank.bank_name}
                          </option>
                        );
                    })}
                  </select>
                  <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10" />
                </div>
                <p className="text-red-500 text-xs">
                  {formik.touched.bankCode &&
                    Boolean(formik.errors.bankCode) &&
                    formik.errors.bankCode}
                </p>
              </div>

              <div className="flex flex-col gap-2 mt-3">
                <label
                  htmlFor="accountNumber"
                  className="text-xs text-white/70"
                >
                  Account Number
                </label>
                <input
                  id="accountNumber"
                  name="accountNumber"
                  type="text"
                  value={formik.values.accountNumber}
                  onChange={formik.handleChange}
                  placeholder=""
                  className="form-input"
                />
                <p className="text-red-500 text-xs">
                  {formik.touched.accountNumber &&
                    Boolean(formik.errors.accountNumber) &&
                    formik.errors.accountNumber}
                </p>
              </div>
            </>
          ) : (
            <></>
          )}

          <div className="flex flex-col gap-2 mt-6">
            <label htmlFor="narration" className="text-sm text-white/70">
              Narration <span className="text-red-500">*</span>
            </label>
            <input
              id="narration"
              name="narration"
              type="text"
              placeholder="Enter narration for this withdrawal"
              value={formik.values.narration}
              onChange={formik.handleChange}
              className="form-input spin-button-none"
            />
            <p className="text-red-500 text-xs">
              {formik.touched.narration &&
                Boolean(formik.errors.narration) &&
                formik.errors.narration}
            </p>
          </div>

          <div className="flex flex-col gap-2 mt-6">
            <label htmlFor="note" className="text-sm text-white/70">
              Note (optional)
            </label>
            <textarea
              name="note"
              id="note"
              cols={10}
              rows={5}
              onChange={formik.handleChange}
              value={formik.values.note}
              className="form-input spin-button-none"
            />
          </div>

          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              name="acceptTerms"
              id="acceptTerms"
              checked={formik.values.acceptTerms}
              onChange={formik.handleChange}
              className="form-checkbox cursor-pointer"
              // checked={formik.values.acceptTerms}
              // onChange={formik.handleChange}
            />
            <label
              htmlFor="acceptTerms"
              className="text-sm text-white/70 cursor-pointer"
            >
              I agree to the{" "}
              <a
                target="_blank"
                href="https://gcbuying.com/terms"
                className="text-white/100 underline hover:text-white/60 transition-all duration-200"
              >
                Terms and Conditions{" "}
              </a>
              &{" "}
              <a
                target="_blank"
                href="https://gcbuying.com/privacy"
                className="text-white/100 underline hover:text-white/60 transition-all duration-200"
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <p className="text-red-500 text-xs">
            {formik.touched.acceptTerms &&
              Boolean(formik.errors.acceptTerms) &&
              formik.errors.acceptTerms}
          </p>

          <button
            type="submit"
            disabled={formik.values.source === SOURCE_PLACEHOLDER}
            className="btn-primary px-4 py-2 rounded-md mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Withdraw Now
          </button>
        </form>
      </div>

      {/* OTP Modal for withdrawal verification */}
      <WithdrawOtpModal onOtpSubmit={handleOtpSubmit} />
      <TopUpModal isOpen={isTopUpOpen} onClose={() => setIsTopUpOpen(false)} />
    </div>
  );
};
